import { CONVENTIONAL_COMMIT_TYPE_LABELS } from "./bundle-ownership";

/**
 * The GitHub **issue type** vocabulary this convention assigns.
 *
 * An issue type is a first-class GitHub field (Epic / Feature / Bug /
 * Task) and is a completely different axis from the `type:*` **label**
 * taxonomy:
 *
 * - `type:<bundle>` / `type:<conventional-commit>` — a *label*. Routing
 *   and dedup signal. Owned by {@link BUNDLE_OWNERSHIP} and set with
 *   `gh issue create --label`.
 * - GitHub issue type — a *field*. Human triage, Epic-relationship
 *   tracking, and reporting signal. `gh issue create` cannot set it, so
 *   it is applied immediately after creation via the
 *   `updateIssueIssueType` GraphQL mutation.
 *
 * Conflating the two is the single most common mistake in this area, so
 * both this module and the prose it renders keep them explicitly apart.
 */
export const GITHUB_ISSUE_TYPES = ["Epic", "Feature", "Bug", "Task"] as const;

export type GithubIssueType = (typeof GITHUB_ISSUE_TYPES)[number];

/**
 * The issue type every title prefix maps to unless it is one of the
 * three explicit exceptions in {@link NON_DEFAULT_TITLE_PREFIX_TYPES}.
 *
 * Every bundle-phase prefix (`company:`, `req:`, `bcm:`, `software:`,
 * …) lands here, which is why agent-enqueued downstream issues are
 * almost always `Task` — the phased pipelines file work items, not
 * features or bug reports.
 */
export const DEFAULT_GITHUB_ISSUE_TYPE: GithubIssueType = "Task";

/**
 * The only title prefixes that map to something other than
 * {@link DEFAULT_GITHUB_ISSUE_TYPE}.
 *
 * `epic:` deliberately has no conventional-commit `type:epic` **label**
 * (epics carry `type:feat`), so it does not appear in
 * {@link CONVENTIONAL_COMMIT_TYPE_LABELS} and is declared here instead.
 */
const NON_DEFAULT_TITLE_PREFIX_TYPES: Readonly<
  Record<string, GithubIssueType>
> = {
  "epic:": "Epic",
  "feat:": "Feature",
  "fix:": "Bug",
};

/**
 * Canonical issue-title-prefix → GitHub issue type map.
 *
 * Derived from {@link CONVENTIONAL_COMMIT_TYPE_LABELS} — the shared
 * conventional-commit vocabulary exported alongside the bundle
 * ownership registry — so the prefix list can never drift from the
 * label list the create-issue workflow stamps. Every conventional-commit
 * prefix defaults to `Task`; the three exceptions are overlaid on top.
 *
 * Prefixes carry their trailing colon (`"feat:"`) to match the way the
 * title conventions write them.
 */
export const GITHUB_ISSUE_TYPE_BY_TITLE_PREFIX: Readonly<
  Record<string, GithubIssueType>
> = buildTitlePrefixTypeMap();

/**
 * Resolve an issue **title** to the GitHub issue type it must carry.
 *
 * Anything that is not one of the four recognised non-default prefixes
 * — including every bundle-phase prefix (`company:research: …`) and a
 * title with no prefix at all — resolves to
 * {@link DEFAULT_GITHUB_ISSUE_TYPE}.
 */
export function githubIssueTypeForTitle(title: string): GithubIssueType {
  const colon = title.indexOf(":");
  if (colon === -1) {
    return DEFAULT_GITHUB_ISSUE_TYPE;
  }
  const prefix = title
    .slice(0, colon + 1)
    .trim()
    .toLowerCase();
  return GITHUB_ISSUE_TYPE_BY_TITLE_PREFIX[prefix] ?? DEFAULT_GITHUB_ISSUE_TYPE;
}

/**
 * Path to the `set-issue-type.sh` helper the `github-workflow` bundle
 * ships. Referenced (never assumed present) by the rendered prose — see
 * {@link renderGithubIssueTypeSectionLines} for the fallback that keeps
 * the recipe working for consumers who exclude that bundle.
 */
export const SET_ISSUE_TYPE_HELPER_PATH =
  ".claude/procedures/set-issue-type.sh";

/**
 * The two-step `updateIssueIssueType` GraphQL flow, rendered as shell.
 *
 * `set-issue-type.sh` wraps exactly this flow, but that helper ships
 * **only** via the `github-workflow` bundle. Any recipe outside that
 * bundle that cited the helper unconditionally would be broken for a
 * consumer running `excludeBundles: ["github-workflow"]`, so the
 * fallback is documented inline in an always-on base rule and every
 * per-filing-site step points at it.
 */
export function renderSetIssueTypeFallbackLines(): Array<string> {
  return [
    "```bash",
    "gh api graphql -f query='query($owner:String!,$repo:String!){repository(owner:$owner,name:$repo){issueTypes(first:50){nodes{id name}}}}' -f owner=<owner> -f repo=<repo>",
    "gh issue view <issue-number> --json id --jq '.id'",
    "gh api graphql -f query='mutation($issueId:ID!,$typeId:ID!){updateIssueIssueType(input:{issueId:$issueId,issueTypeId:$typeId}){issue{number issueType{name}}}}' -f issueId=<issue-node-id> -f typeId=<issue-type-node-id>",
    "```",
  ];
}

/**
 * Render the **GitHub Issue Type** section of the always-on
 * `issue-conventions` rule.
 *
 * The section is the single canonical answer to "how does an agent set
 * an issue's type?", and it is rendered into an `ALWAYS`-scoped base
 * rule precisely so every downstream filing site can cite it in one
 * line regardless of which optional bundles the consumer enabled.
 *
 * It documents both paths deliberately:
 *
 * 1. The `set-issue-type.sh` one-liner, when `github-workflow` is
 *    active.
 * 2. The inline GraphQL fallback, when it is not.
 */
export function renderGithubIssueTypeSectionLines(): Array<string> {
  const defaultPrefixes = Object.keys(GITHUB_ISSUE_TYPE_BY_TITLE_PREFIX)
    .filter(
      (prefix) =>
        GITHUB_ISSUE_TYPE_BY_TITLE_PREFIX[prefix] === DEFAULT_GITHUB_ISSUE_TYPE,
    )
    .sort()
    .map((prefix) => `\`${prefix}\``)
    .join(", ");

  return [
    "## GitHub Issue Type",
    "",
    "A GitHub **issue type** is a distinct field from the `type:*` **label**:",
    "the label routes and dedups, the issue type drives triage and Epic",
    "tracking. Setting one does not set the other. Derive it from the",
    "**title prefix**:",
    "",
    "| Prefix | GitHub Issue Type |",
    "|--------|------------------|",
    "| `epic:` | Epic |",
    "| `feat:` | Feature |",
    "| `fix:` | Bug |",
    `| ${defaultPrefixes} | ${DEFAULT_GITHUB_ISSUE_TYPE} |`,
    "| any bundle-phase prefix (`company:`, `req:`, …), or no prefix | Task |",
    "",
    "### Assigning the GitHub issue type",
    "",
    "`gh issue create` **cannot** set the issue type — there is no `--type`",
    "flag — so assign it in a follow-up call immediately after creation, for",
    "every issue an agent files (auto-enqueued downstream issues included).",
    "An issue that skips that call stays untyped forever. The",
    "`github-workflow` bundle ships a one-step helper:",
    "",
    "```bash",
    `${SET_ISSUE_TYPE_HELPER_PATH} <issue-number> <${GITHUB_ISSUE_TYPES.join("|")}>`,
    "```",
    "",
    "When that helper is absent (the bundle is excluded), run the",
    "`updateIssueIssueType` GraphQL flow it wraps:",
    "",
    ...renderSetIssueTypeFallbackLines(),
  ];
}

/**
 * Render the title-prefix → issue-type mapping as a markdown bullet
 * list, for recipes that present it inline rather than as a table (the
 * interactive create-issue workflow's step 3).
 *
 * Grouping matches the table in {@link renderGithubIssueTypeSectionLines}:
 * one bullet per non-default prefix, then a single bullet collapsing
 * every prefix that maps to {@link DEFAULT_GITHUB_ISSUE_TYPE}.
 */
export function renderTitlePrefixTypeBullets(indent = "   "): Array<string> {
  const nonDefault = Object.entries(NON_DEFAULT_TITLE_PREFIX_TYPES).map(
    ([prefix, issueType]) => `${indent}- \`${prefix}\` → ${issueType}`,
  );
  const defaults = Object.keys(GITHUB_ISSUE_TYPE_BY_TITLE_PREFIX)
    .filter(
      (prefix) =>
        GITHUB_ISSUE_TYPE_BY_TITLE_PREFIX[prefix] === DEFAULT_GITHUB_ISSUE_TYPE,
    )
    .sort()
    .map((prefix) => `\`${prefix}\``)
    .join(", ");
  return [
    ...nonDefault,
    `${indent}- ${defaults} → ${DEFAULT_GITHUB_ISSUE_TYPE}`,
    `${indent}- any bundle-phase prefix (\`company:\`, \`req:\`, \`bcm:\`, …) → ${DEFAULT_GITHUB_ISSUE_TYPE}`,
  ];
}

/** String form of {@link renderGithubIssueTypeSectionLines}. */
export function renderGithubIssueTypeSection(): string {
  return renderGithubIssueTypeSectionLines().join("\n");
}

/** Options for {@link renderIssueTypeAssignmentStep}. */
export interface IssueTypeAssignmentStepOptions {
  /**
   * Leading whitespace prepended to every rendered line so the step
   * nests correctly under the numbered/bulleted filing recipe it
   * follows. Defaults to the three spaces a top-level numbered list
   * item continues with.
   */
  readonly indent?: string;
  /**
   * The GitHub issue type the filed issue must carry. Defaults to
   * {@link DEFAULT_GITHUB_ISSUE_TYPE}, which is correct for every
   * bundle-phase-prefixed downstream issue.
   */
  readonly issueType?: GithubIssueType;
  /**
   * Render the step as a markdown list item (`- …` with hanging
   * continuation lines) instead of a paragraph. Used at the handful of
   * filing recipes that specify the issue with a bullet list rather
   * than numbered prose.
   */
  readonly bullet?: boolean;
}

/**
 * Render the compact "now set the issue type" step appended to every
 * bundle-shipped downstream filing recipe.
 *
 * Kept deliberately short: it appears at ~45 filing sites across the
 * phased-pipeline bundles, so it names the concrete type, calls out that
 * the `type:*` label is a different field, gives the command, and
 * delegates the fallback to the always-on `issue-conventions` rule
 * rather than re-inlining the GraphQL flow at every site.
 */
export function renderIssueTypeAssignmentStep(
  options: IssueTypeAssignmentStepOptions = {},
): Array<string> {
  const indent = options.indent ?? "   ";
  const issueType = options.issueType ?? DEFAULT_GITHUB_ISSUE_TYPE;
  const lead = options.bullet ? `${indent}- ` : indent;
  const cont = options.bullet ? `${indent}  ` : indent;
  return [
    `${lead}Then assign the new issue's **GitHub issue type** (\`${issueType}\`) —`,
    `${cont}the \`type:*\` label does not set it:`,
    `${cont}\`${SET_ISSUE_TYPE_HELPER_PATH} <issue-number> ${issueType}\`. See`,
    `${cont}**Assigning the GitHub issue type** for the no-helper fallback.`,
  ];
}

/**
 * Render the phase-wide variant of {@link renderIssueTypeAssignmentStep}
 * for a workflow phase that files several kinds of issue across several
 * steps, where repeating the per-recipe step at each one would bloat the
 * prompt without adding information.
 */
export function renderIssueTypeAssignmentBlanket(
  indent = "",
  issueType: GithubIssueType = DEFAULT_GITHUB_ISSUE_TYPE,
): Array<string> {
  return [
    `${indent}**Every** issue this phase files needs a **GitHub issue type**`,
    `${indent}assigned right after creation — the \`type:*\` label does not set`,
    `${indent}it, and \`gh issue create\` cannot:`,
    `${indent}\`${SET_ISSUE_TYPE_HELPER_PATH} <issue-number> ${issueType}\`. See`,
    `${indent}**Assigning the GitHub issue type** for the prefix table (nearly`,
    `${indent}every downstream issue is a \`${issueType}\`) and the no-helper fallback.`,
  ];
}

/**
 * Build {@link GITHUB_ISSUE_TYPE_BY_TITLE_PREFIX} from the shared
 * conventional-commit label vocabulary plus the explicit exceptions.
 */
function buildTitlePrefixTypeMap(): Readonly<Record<string, GithubIssueType>> {
  const map: Record<string, GithubIssueType> = {};
  for (const label of CONVENTIONAL_COMMIT_TYPE_LABELS) {
    map[`${label.slice("type:".length)}:`] = DEFAULT_GITHUB_ISSUE_TYPE;
  }
  for (const [prefix, issueType] of Object.entries(
    NON_DEFAULT_TITLE_PREFIX_TYPES,
  )) {
    map[prefix] = issueType;
  }
  return map;
}
